import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  DocumentIcon,
  UserCircleIcon,
  PencilIcon,
  TrashIcon,
  MicrophoneIcon,
  StopIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import api, { getImageUrl } from "../services/api";

interface Message {
  id: number;
  sender_id: number;
  receiver_id: number | null;
  message: string;
  attachment_url: string | null;
  attachment_type: string | null;
  is_read: boolean;
  created_at: string;
  updated_at: string | null;
  sender_name: string;
  sender_profile_image: string | null;
  receiver_name: string | null;
  receiver_profile_image: string | null;
}

interface ChatPopupProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUserId?: number;
}

const ChatPopup: React.FC<ChatPopupProps> = ({
  isOpen,
  onClose,
  selectedUserId,
}) => {
  const { user, isAdmin } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagePollingRef = useRef<NodeJS.Timeout | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const loadMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const params =
        isAdmin && selectedUserId ? { otherUserId: selectedUserId } : {};
      const response = await api.get("/messages", { params });
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Error loading messages:", error);
      // For visitors, we don't need to show error for loading messages
      if (!user) {
        setMessages([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, selectedUserId, user]);

  useEffect(() => {
    if (isOpen && user) {
      loadMessages();
      // Poll for new messages every 5 seconds
      messagePollingRef.current = setInterval(loadMessages, 5000);
    }

    return () => {
      if (messagePollingRef.current) {
        clearInterval(messagePollingRef.current);
      }
      // Stop any playing audio when component unmounts
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlayingVoice(null);
    };
  }, [isOpen, user, selectedUserId, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!newMessage.trim() && !selectedFile) || isSending) return;

    // Check if user is authenticated
    if (!user) {
      showError("ກະລຸນາເຂົ້າສູ່ລະບົບກ່ອນສົ່ງຂໍ້ຄວາມ");
      return;
    }

    // Check if admin user has selected a recipient
    if (isAdmin && !selectedUserId) {
      showError("ກະລຸນາເລືອກຜູ້ຮັບຂໍ້ຄວາມກ່ອນສົ່ງ");
      return;
    }

    try {
      setIsSending(true);
      const formData = new FormData();

      // If there's no message text but there's a file, send a default message
      const messageText =
        newMessage.trim() || (selectedFile ? "📎 Attachment" : "");
      formData.append("message", messageText);

      console.log("Sending message with:", {
        isAdmin,
        selectedUserId,
        message: messageText,
      });

      if (isAdmin && selectedUserId) {
        formData.append("receiverId", selectedUserId.toString());
        console.log("Added receiverId to formData:", selectedUserId);
      }

      if (selectedFile) {
        formData.append("attachment", selectedFile);
      }

      const response = await api.post("/messages", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Message sent successfully:", response.data);

      // Add the new message to the list
      setMessages([...messages, response.data.data]);
      setNewMessage("");
      setSelectedFile(null);
    } catch (error: any) {
      console.error("Error sending message:", error);
      console.error("Error response:", error.response?.data);

      // Show more specific error messages
      let errorMessage = "ການສົ່ງຂໍ້ຄວາມລົ້ມເຫລວ. ";

      if (error.response) {
        // Server responded with an error
        if (error.response.status === 401) {
          errorMessage += "ທ່ານບໍ່ໄດ້ຮັບການຢັ້ງຢືນ. ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່.";
        } else if (error.response.status === 404) {
          errorMessage +=
            "ບໍ່ພົບ message endpoint. ກະລຸນາກວດສອບເຊີເວີກຳລັງໃຊ້ງານຢູ່ບໍ່.";
        } else {
          errorMessage +=
            error.response.data?.message || "Unknown server error";
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage +=
          "ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີໄດ້. ກະລຸນາກວດສອບ backend ກຳລັງໃຊ້ງານຢູ່ບໍ່.";
      } else {
        // Something else happened
        errorMessage += error.message || "Unknown error occurred.";
      }

      showError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleEditMessage = (message: Message) => {
    setEditingMessageId(message.id);
    setEditMessageText(message.message);
    setIsEditing(true);
    // Focus the edit input after a short delay
    setTimeout(() => {
      editInputRef.current?.focus();
    }, 100);
  };

  const handleUpdateMessage = async () => {
    if (!editingMessageId || !editMessageText.trim()) return;

    try {
      setIsEditing(true);
      const response = await api.put(`/messages/${editingMessageId}`, {
        message: editMessageText.trim(),
      });

      // Update the message in the list
      setMessages(
        messages.map((msg) =>
          msg.id === editingMessageId
            ? {
                ...msg,
                message: editMessageText.trim(),
                updated_at: new Date().toISOString(),
              }
            : msg,
        ),
      );

      setEditingMessageId(null);
      setEditMessageText("");
    } catch (error: any) {
      console.error("Error updating message:", error);
      let errorMessage = "ການອັບເດດຂໍ້ຄວາມລົ້ມເຫລວ. ";

      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || "Unknown error occurred.";
      }

      showError(errorMessage);
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    setMessageToDelete(messageId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete || isDeleting) return;

    try {
      setIsDeleting(true);

      // Stop any playing audio if this message is being deleted
      if (isPlayingVoice === messageToDelete) {
        stopVoiceMessage();
      }

      await api.delete(`/messages/${messageToDelete}`);

      // Remove the message from the list
      setMessages(messages.filter((msg) => msg.id !== messageToDelete));
    } catch (error: any) {
      console.error("Error deleting message:", error);
      let errorMessage = "ການລຶບຂໍ້ຄວາມລົ້ມເຫລວ. ";

      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || "Unknown error occurred.";
      }

      showError(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setMessageToDelete(null);
    }
  };

  const cancelDeleteMessage = () => {
    setShowDeleteDialog(false);
    setMessageToDelete(null);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  };

  const closeErrorDialog = () => {
    setShowErrorDialog(false);
    setErrorMessage("");
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditMessageText("");
    setIsEditing(false);
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      showError("ບໍ່ສາມາດເຂົ້າເຖິງໄມໂຄຣໂຟນໄດ້. ກະລຸນາກວດສອບສິດທີ່.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingTime(0);
      setAudioBlob(null);

      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob) return;

    // Check if user is authenticated
    if (!user) {
      showError("ກະລຸນາເຂົ້າສູ່ລະບົບກ່ອນສົ່ງຂໍ້ຄວາມ");
      return;
    }

    // Check if admin user has selected a recipient
    if (isAdmin && !selectedUserId) {
      showError("ກະລຸນາເລືອກຜູ້ຮັບຂໍ້ຄວາມກ່ອນສົ່ງ");
      return;
    }

    try {
      setIsSending(true);
      const formData = new FormData();

      // Create a file from the blob
      const audioFile = new File([audioBlob], `voice-${Date.now()}.webm`, {
        type: "audio/webm",
      });
      formData.append("attachment", audioFile);
      formData.append("message", "🎤 Voice message");

      if (isAdmin && selectedUserId) {
        formData.append("receiverId", selectedUserId.toString());
      }

      const response = await api.post("/messages", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setMessages([...messages, response.data.data]);
      setAudioBlob(null);
      setRecordingTime(0);
    } catch (error: any) {
      console.error("Error sending voice message:", error);
      showError("ການສົ່ງຂໍ້ຄວາມສຽງລົ້ມເຫລວ");
    } finally {
      setIsSending(false);
    }
  };

  const playVoiceMessage = (audioUrl: string, messageId: number) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(getImageUrl(audioUrl));
    audioRef.current = audio;
    setIsPlayingVoice(messageId);

    audio.onended = () => {
      setIsPlayingVoice(null);
    };

    audio.onerror = () => {
      setIsPlayingVoice(null);
      showError("ຂໍ້ຜິດພາດໃນການຫຼິ້ນຂໍ້ຄວາມສຽງ");
    };

    audio.play();
  };

  const stopVoiceMessage = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlayingVoice(null);
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        showError("ຂະໜາດໄຟລ໌ຕ້ອງນ້ອຍກວ່າ 50MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const canEditMessage = (message: Message) => {
    // Only sender can edit their own messages
    if (
      message.sender_id !== user?.id &&
      !(message.sender_id === null && !user)
    )
      return false;

    // Check if message is within 24 hours
    const messageTime = new Date(message.created_at);
    const now = new Date();
    const hoursDiff =
      (now.getTime() - messageTime.getTime()) / (1000 * 60 * 60);

    return hoursDiff <= 24;
  };

  const canDeleteMessage = (message: Message) => {
    // Only sender can delete their own messages
    if (
      message.sender_id !== user?.id &&
      !(message.sender_id === null && !user)
    )
      return false;

    // Check if message is within 24 hours
    const messageTime = new Date(message.created_at);
    const now = new Date();
    const hoursDiff =
      (now.getTime() - messageTime.getTime()) / (1000 * 60 * 60);

    return hoursDiff <= 24;
  };

  const renderAttachment = (message: Message) => {
    if (!message.attachment_url) return null;

    const url = getImageUrl(message.attachment_url);
    const type = message.attachment_type?.toLowerCase() || "";

    if (type.startsWith("image")) {
      return (
        <img
          src={url}
          alt="Attachment"
          className="w-full max-w-[150px] sm:max-w-xs max-h-48 object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            window.open(url, "_blank");
          }}
        />
      );
    }

    if (type.startsWith("video")) {
      return (
        <video
          src={url}
          controls
          className="w-full max-w-[150px] sm:max-w-xs max-h-48 rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (type.startsWith("audio")) {
      return (
        <div className="flex items-center space-x-2 bg-gray-100 p-3 rounded-lg min-w-[200px]">
          {isPlayingVoice === message.id ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                stopVoiceMessage();
              }}
              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex-shrink-0"
              title="Stop"
            >
              <StopIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                playVoiceMessage(message.attachment_url!, message.id);
              }}
              className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors flex-shrink-0"
              title="Play"
            >
              <PlayIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex flex-col overflow-hidden">
            <span className="text-sm font-medium text-gray-700 truncate">
              Voice Message
            </span>
            <span className="text-xs text-gray-500">Click to play</span>
          </div>
        </div>
      );
    }

    if (type.includes("pdf") || type.includes("document")) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-2 bg-gray-100 p-3 rounded-lg hover:bg-gray-200 transition-colors max-w-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <DocumentIcon className="w-6 h-6 text-gray-600 flex-shrink-0" />
          <span className="text-sm text-gray-700 truncate overflow-hidden">
            View Document
          </span>
        </a>
      );
    }

    // Default fallback
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 hover:text-indigo-800 underline break-words"
        onClick={(e) => e.stopPropagation()}
      >
        View Attachment
      </a>
    );
  };

  // Cleanup when chat is closed
  useEffect(() => {
    if (!isOpen) {
      // Stop any playing audio when chat is closed
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlayingVoice(null);
      // Reset recording state
      if (isRecording) {
        cancelRecording();
      }
    }
  }, [isOpen]);

  // Return null if not open ONLY if we want to destroy state (but for slide animation we want to keep it mounted usually)
  // However, for simplicity and performance, effectively hiding it via CSS is better for the transition.
  // But we need to handle the initial render or "hidden" state properly.
  // We'll use the classes to toggle visibility.

  return (
    <>
      {/* Delete Confirmation Dialog - Outside chat container */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Message
                </h3>
                <p className="text-sm text-gray-500">
                  ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.
                </p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຂໍ້ຄວາມນີ້? ການກະທຳນີ້ຈະລຶບຂໍ້ຄວາມ ແລະ
              ໄຟລ໌ແນບທີ່ມາກັບມັນຖາວອນ.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteMessage}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ຍົກເລີກ
              </button>
              <button
                onClick={confirmDeleteMessage}
                disabled={isDeleting}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over Chat Panel */}
      <div
        className={`fixed inset-y-0 right-[2px] z-[70] w-full sm:w-[400px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full flex flex-col bg-gray-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shadow-sm">
            <h3 className="text-base font-semibold text-gray-800 flex items-center">
              <span>ສົ່ງຂໍ້ຄວາມເພື່ອສອບຖາມ</span>
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
              title="Close chat"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
            {!user ? (
              <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 max-w-xs mx-auto">
                  <h3 className="text-lg font-bold text-blue-900 mb-2">
                    ກະລຸນາເຂົ້າສູ່ລະບົບ
                  </h3>
                  <p className="text-sm text-blue-700 mb-6">
                    ເພື່ອສົ່ງຂໍ້ຄວາມຫາທີມງານ ກະລຸນາເຂົ້າສູ່ລະບົບກ່ອນ
                  </p>
                  <button
                    onClick={() => (window.location.href = "/login")}
                    className="w-full bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
                  >
                    ເຂົ້າສູ່ລະບົບ
                  </button>
                </div>
              </div>
            ) : isLoading && messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <PaperAirplaneIcon className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-gray-900">
                  ຍິນດີຕ້ອນຮັບ!
                </p>
                <p className="text-sm mt-1">
                  ເລີ່ມການສົນທະນາກັບທີມງານຂອງພວກເຮົາ
                </p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwnMessage =
                  message.sender_id === user?.id ||
                  (!user && message.sender_id === null);
                const isEditing = editingMessageId === message.id;

                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`flex max-w-[90%] sm:max-w-[85%] ${isOwnMessage ? "flex-row-reverse" : "flex-row"} space-x-1 sm:space-x-2`}
                    >
                      {/* Profile Image */}
                      <div className="flex-shrink-0">
                        {message.sender_profile_image ? (
                          <img
                            src={getImageUrl(message.sender_profile_image)}
                            alt={message.sender_name}
                            className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 rounded-full"
                          />
                        ) : (
                          <UserCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-gray-400" />
                        )}
                      </div>

                      {/* Message Content */}
                      <div
                        className={`flex flex-col ${isOwnMessage ? "items-end" : "items-start"}`}
                      >
                        <div className="text-xs text-gray-500 mb-1">
                          {message.sender_name} •{" "}
                          {formatTime(message.created_at)}
                          {message.updated_at &&
                            message.updated_at !== message.created_at && (
                              <span className="ml-1 text-gray-400">
                                (edited)
                              </span>
                            )}
                        </div>

                        {isEditing ? (
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <input
                              ref={editInputRef}
                              type="text"
                              value={editMessageText}
                              onChange={(e) =>
                                setEditMessageText(e.target.value)
                              }
                              className="flex-1 p-1.5 sm:p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs sm:text-sm"
                              placeholder="Edit message..."
                              aria-label="Edit message"
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateMessage();
                                } else if (e.key === "Escape") {
                                  cancelEdit();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={handleUpdateMessage}
                              disabled={!editMessageText.trim()}
                              className="p-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <PaperAirplaneIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                              title="ຍົກເລີກ"
                            >
                              <XMarkIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="relative group">
                            <div
                              className={`rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm break-words ${
                                isOwnMessage
                                  ? "bg-indigo-600 text-white"
                                  : "bg-gray-100 text-gray-900"
                              }`}
                            >
                              {message.message}
                            </div>

                            {message.attachment_url && (
                              <div className="mt-1 sm:mt-2">
                                {renderAttachment(message)}
                              </div>
                            )}

                            {/* Edit/Delete buttons for own messages */}
                            {isOwnMessage && (
                              <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                                {canEditMessage(message) && (
                                  <button
                                    onClick={() => handleEditMessage(message)}
                                    className="p-0.5 sm:p-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                    title="Edit message"
                                  >
                                    <PencilIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                                  </button>
                                )}
                                {canDeleteMessage(message) && (
                                  <button
                                    onClick={() =>
                                      handleDeleteMessage(message.id)
                                    }
                                    disabled={isDeleting}
                                    className="p-0.5 sm:p-1 bg-red-200 text-red-700 rounded hover:bg-red-300 disabled:opacity-50"
                                    title="Delete message"
                                  >
                                    <TrashIcon className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form
            onSubmit={handleSendMessage}
            className="p-3 sm:p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white shadow-inner"
          >
            {!user ? (
              <div className="text-center py-3">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-gray-600 text-sm font-medium">
                    ກະລຸນາເຂົ້າສູ່ລະບົບເພື່ອສົ່ງຂໍ້ຄວາມ
                  </p>
                </div>
              </div>
            ) : isAdmin && !selectedUserId ? (
              <div className="text-center py-3">
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-amber-700 text-sm font-medium">
                    ກະລຸນາເລືອກຜູ້ຮັບຂໍ້ຄວາມກ່ອນສົ່ງ
                  </p>
                </div>
              </div>
            ) : (
              <>
                {selectedFile && (
                  <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-2">
                      <PaperClipIcon className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium truncate max-w-[200px]">
                        {selectedFile.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-all duration-200"
                      title="Remove attachment"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex items-end space-x-2 bg-white rounded-xl border-2 border-gray-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-200 transition-all duration-200 p-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.txt,.mp4,.avi,.mov,.wmv"
                    aria-label="ເລືອກໄຟລ໌ເພື່ອແນບ"
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 flex-shrink-0 group"
                    title="Attach file"
                    aria-label="Attach file"
                  >
                    <PaperClipIcon className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                  </button>

                  {/* Voice Recording Section */}
                  {isRecording ? (
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-red-50 to-pink-50 p-2 rounded-lg flex-shrink-0 border border-red-200">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-sm text-red-700 font-medium">
                          {formatRecordingTime(recordingTime)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 shadow-sm"
                        title="Stop recording"
                      >
                        <StopIcon className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelRecording}
                        className="p-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 shadow-sm"
                        title="ຍົກເລີກຂໍ້ຄວາມສຽງ"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : audioBlob ? (
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-green-50 to-emerald-50 p-2 rounded-lg flex-shrink-0 border border-green-200">
                      <span className="text-sm text-green-700 font-medium">
                        Voice recorded
                      </span>
                      <button
                        type="button"
                        onClick={sendVoiceMessage}
                        disabled={isSending}
                        className="p-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors duration-200 shadow-sm"
                        title="Send voice message"
                      >
                        <PaperAirplaneIcon className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelRecording}
                        className="p-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 shadow-sm"
                        title="ຍົກເລີກຂໍ້ຄວາມສຽງ"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex-shrink-0 group"
                      title="ບັນທຶກຂໍ້ຄວາມສຽງ"
                      aria-label="ບັນທຶກຂໍ້ຄວາມສຽງ"
                    >
                      <MicrophoneIcon className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
                    </button>
                  )}

                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onFocus={() => setTimeout(scrollToBottom, 50)}
                    placeholder={
                      user
                        ? "ພິມຂໍ້ຄວາມເພື່ອສອບຖາມ..."
                        : "ພິມຂໍ້ຄວາມມາຫາພວກເຮົາ..."
                    }
                    className="flex-1 min-w-0 p-2 border-0 focus:outline-none focus:ring-0 text-sm placeholder-gray-400 bg-transparent"
                    disabled={isSending || isRecording}
                  />

                  <button
                    type="submit"
                    disabled={
                      (!newMessage.trim() && !selectedFile && !audioBlob) ||
                      isSending ||
                      isRecording
                    }
                    className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex-shrink-0 group"
                    title="Send message"
                    aria-label="Send message"
                  >
                    <PaperAirplaneIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Error</h3>
                <p className="text-sm text-gray-500">Something went wrong</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">{errorMessage}</p>

            <div className="flex justify-end">
              <button
                onClick={closeErrorDialog}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPopup;
